import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { request } from '../../services/fetch';
import { useUser } from '../Context/UserContext';
import { Spinner } from '..';

export default function LegalAcceptanceGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [required, setRequired] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Falha ao verificar (rede, backend fora do ar num restart, etc.) é DIFERENTE de "precisa
  // aceitar de novo" — antes o catch tratava qualquer erro como requiresLegalAcceptance=true,
  // o que exibia o modal de aceite pra quem já tinha aceitado, sempre que a checagem esbarrava
  // numa falha transitória. Erro agora tem seu próprio estado, com retry, sem mexer em
  // required/accepted.
  const [error, setError] = useState(false);
  const { user } = useUser();
  const toast = useToast();

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request('api/legal/acceptance-status');
      setRequired(Boolean(data.requiresLegalAcceptance));
      setAccepted(!data.requiresLegalAcceptance);
      setError(false);
    } catch (err) {
      toast({
        title: 'Erro ao verificar aceite',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadStatus();
  }, [user]);

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      const data = await request('api/legal/accept', { method: 'POST', body: '{}' });
      setRequired(false);
      setAccepted(true);
      toast({
        title: 'Aceite registrado',
        description: data.acceptance?.acceptedAt
          ? `Registrado em ${new Date(data.acceptance.acceptedAt).toLocaleString('pt-BR')}`
          : 'Você pode utilizar o sistema.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Não foi possível registrar o aceite',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner  />
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="100vh">
        <VStack spacing={4}>
          <Text color="gray.600">Não foi possível verificar o status de aceite dos termos.</Text>
          <Button colorScheme="teal" onClick={loadStatus}>Tentar novamente</Button>
        </VStack>
      </Center>
    );
  }

  if (!accepted && required) {
    return (
      <Modal isOpen onClose={() => { }} closeOnOverlayClick={false} closeOnEsc={false} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent mx={4}>
          <ModalHeader>
            <Heading size="md" color="teal.500">
              Aceite dos termos legais
            </Heading>
            <Text fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
              Antes de continuar, leia e aceite os documentos abaixo.
            </Text>
          </ModalHeader>
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Flex gap={4} flexWrap="wrap">
                <Link as={RouterLink} to="/termos-de-servico" target="_blank" color="teal.600" fontWeight="medium">
                  Termos de Serviço ↗
                </Link>
                <Link as={RouterLink} to="/politica-de-privacidade" target="_blank" color="teal.600" fontWeight="medium">
                  Política de Privacidade ↗
                </Link>
              </Flex>
              <Checkbox
                isChecked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                colorScheme="teal"
              >
                Li e aceito os Termos de Serviço e a Política de Privacidade do Osteomedic.
              </Checkbox>
              <Box fontSize="xs" color="gray.500">
                O aceite será registrado em log com data, hora e identificação do usuário.
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="teal"
              w="full"
              isDisabled={!checked}
              isLoading={submitting}
              onClick={handleAccept}
            >
              Aceitar e continuar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return children;
}
